import { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getShortlinkByPath } from '../../lib/shortlinkService';
import { recordClick } from '../../lib/analyticsService';
import { databaseService } from '../../lib/appwrite';
import { Loader } from 'lucide-react';

// Lazy load NotFound404 for broken links
const NotFound404 = lazy(() => import('../../pages/NotFound404'));

export default function ShortlinkRedirect() {
  const { path, prefix } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isShortlink, setIsShortlink] = useState(false);
  const [shortlinkData, setShortlinkData] = useState(null);

  useEffect(() => {
    handleRedirect();
  }, [path, prefix]);

  const handleRedirect = async () => {
    try {
      // Build the full path (with prefix if present)
      const fullPath = prefix ? `${prefix}/${path}` : path;
      
      // Check if it's a shortlink
      const shortlink = await getShortlinkByPath(fullPath);

      if (shortlink) {
        setIsShortlink(true);
        setShortlinkData(shortlink);

        // Record analytics asynchronously (don't wait for it)
        recordClick(shortlink.$id, {
          referrer: document.referrer,
          userAgent: navigator.userAgent,
          customDomain: false
        }).catch(err => console.error('Error recording analytics:', err));

        // Redirect to destination URL
        window.location.replace(shortlink.destinationUrl);
        return;
      }

      // If not a shortlink, check for blog with custom slug (useProjectPrefix = false)
      try {
        const blogsResult = await databaseService.listDocuments('blogs');
        if (blogsResult.success) {
          const matchingBlog = blogsResult.data.documents.find(
            blog => blog.customSlug === path && blog.useProjectPrefix === false
          );
          if (matchingBlog) {
            navigate(`/blog/${matchingBlog.customSlug}`, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error('Error checking blogs:', err);
      }

      // Check for project with custom slug (useProjectPrefix = false)
      try {
        const projectsResult = await databaseService.listDocuments('projects');
        if (projectsResult.success) {
          const matchingProject = projectsResult.data.documents.find(
            project => project.customSlug === path && project.useProjectPrefix === false
          );
          if (matchingProject) {
            navigate(`/project/${matchingProject.customSlug}`, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error('Error checking projects:', err);
      }

      // Check for product with custom slug (useProductPrefix = false)
      try {
        const productsResult = await databaseService.listDocuments('shop_products');
        if (productsResult.success) {
          const matchingProduct = productsResult.data.documents.find(
            product => product.customSlug === path && product.useProductPrefix === false
          );
          if (matchingProduct) {
            navigate(`/product/${matchingProduct.customSlug}`, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error('Error checking products:', err);
      }

      // No match found - show 404 page
      setChecking(false);
      setIsShortlink(false);
    } catch (error) {
      console.error('Error handling shortlink redirect:', error);
      setChecking(false);
      setIsShortlink(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#105652' }} />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not a shortlink, show 404 page
  if (!isShortlink) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#105652' }}></div>
        </div>
      }>
        <NotFound404 />
      </Suspense>
    );
  }

  // If it is a shortlink, show redirecting message
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#105652' }} />
        <p className="text-gray-600">Redirecting...</p>
        {shortlinkData?.title && (
          <p className="text-sm text-gray-500 mt-2">{shortlinkData.title}</p>
        )}
      </div>
    </div>
  );
}
